import React, { useState, useEffect } from "react";
import { MdAdd, MdClose, MdInfoOutline, MdEdit, MdDelete } from "react-icons/md";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import InfoModal from "./InfoModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import "./TicketFormTab.css";

const TicketFormTab = ({ brandId, protectedFetch }) => {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [typeValue, setTypeValue] = useState("");
  const [reasons, setReasons] = useState([]);
  const [reasonInput, setReasonInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [infoModalData, setInfoModalData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    setLoading(true);
    reloadData()
      .catch((err) => {
        console.log("Error fetching ticket reasons:", err);
        showToast("Failed to load ticket reasons", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId]);

  const handleAddNew = () => {
    setEditingIndex(null);
    setTypeValue("");
    setReasons([]);
    setReasonInput("");
    setShowForm(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const rowTypes = rows[index].types || [];
    setTypeValue(rowTypes.length > 0 ? rowTypes[0].label : "");
    setReasons(rows[index].reasons.map((r) => r?.reason || r));
    setReasonInput("");
    setShowForm(true);
  };

  const handleDelete = (index) => {
    setDeleteTarget({ index, row: rows[index] });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingIndex(null);
    setTypeValue("");
    setReasons([]);
    setReasonInput("");
  };

  const reloadData = () => {
    return protectedFetch(
      apiEndpoints.ticket.ticketReasonBrands(brandId),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        const data = res?.data || res;
        if (Array.isArray(data) && data.length > 0) {
          const grouped = {};
          data.forEach((item) => {
            if (!grouped[item.category]) {
              grouped[item.category] = [];
            }
            grouped[item.category].push({
              id: item.id,
              reason: item.reason,
              order: item.order,
            });
          });
          Object.values(grouped).forEach((reasons) => {
            reasons.sort((a, b) => a.order - b.order);
          });
          const fetchedRows = Object.entries(grouped).map(
            ([category, reasons]) => ({
              types: [{ value: generateValue(category), label: category }],
              reasons,
            }),
          );
          setRows(fetchedRows);
        } else {
          setRows([]);
        }
      });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { index, row } = deleteTarget;
    const categoryName =
      row.types && row.types.length > 0 ? row.types[0].label : "Unknown";

    setDeleting(true);
    const deletePromises = row.reasons
      .filter((r) => r.id)
      .map((r) =>
        protectedFetch(
          apiEndpoints.ticket.deleteTicketReason(r.id),
          "DELETE",
          null,
          errorFunctions.checkResponseSent,
        ),
      );

    try {
      await Promise.all(deletePromises);
      setRows((prev) => prev.filter((_, i) => i !== index));
      showToast(`"${categoryName}" deleted successfully`, "success");
    } catch {
      showToast(`Failed to delete "${categoryName}"`, "error");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const generateValue = (label) =>
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const handleAddReason = () => {
    const trimmed = reasonInput.trim();
    if (trimmed && !reasons.includes(trimmed)) {
      setReasons((prev) => [...prev, trimmed]);
    }
    setReasonInput("");
  };

  const handleRemoveReason = (index) => {
    setReasons((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReasonKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddReason();
    }
  };

  const handleReasonDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
  };

  const handleReasonDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragIndex !== null && dragIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleReasonDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const updated = [...reasons];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setReasons(updated);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleReasonDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    const trimmedType = typeValue.trim() ? typeValue.trim() : "";
    if (reasons.length === 0) return;

    setSaving(true);

    if (editingIndex !== null) {
      // Edit mode: PATCH existing / POST new reasons
      const originalReasons = rows[editingIndex].reasons;
      const originalMap = new Map(originalReasons.map((r) => [r.reason, r.id]));

      const promises = [];
      reasons.forEach((reason, idx) => {
        const payload = {
          brand: Number(brandId),
          category: trimmedType,
          reason,
          order: idx,
        };
        if (originalMap.has(reason)) {
          promises.push(
            protectedFetch(
              apiEndpoints.ticket.updateTicketReason(originalMap.get(reason)),
              "PATCH",
              payload,
              errorFunctions.checkResponseThenJson,
            ),
          );
          originalMap.delete(reason);
        } else {
          promises.push(
            protectedFetch(
              apiEndpoints.ticket.ticketReasonBrands(),
              "POST",
              payload,
              errorFunctions.checkResponseThenJson,
            ),
          );
        }
      });

      // Delete removed reasons
      originalMap.forEach((id) => {
        promises.push(
          protectedFetch(
            apiEndpoints.ticket.deleteTicketReason(id),
            "DELETE",
            null,
            errorFunctions.checkResponseSent,
          ),
        );
      });

      try {
        await Promise.all(promises);
        await reloadData();
        showToast("Ticket reasons updated successfully", "success");
        setShowForm(false);
        setEditingIndex(null);
        setTypeValue("");
        setReasons([]);
        setReasonInput("");
      } catch {
        showToast("Failed to update ticket reasons", "error");
      } finally {
        setSaving(false);
      }
    } else {
      // New category: existing POST behavior
      const payload = reasons.map((reason, idx) => ({
        brand: Number(brandId),
        category: trimmedType,
        reason,
        order: idx,
      }));

      protectedFetch(
        apiEndpoints.ticket.ticketReasonBrands(),
        "POST",
        payload,
        errorFunctions.checkResponseThenJson,
      )
        .then(async () => {
          await reloadData();
          showToast("Ticket reasons saved successfully", "success");
          setShowForm(false);
          setEditingIndex(null);
          setTypeValue("");
          setReasons([]);
          setReasonInput("");
        })
        .catch(() => {
          showToast("Failed to save ticket reasons", "error");
        })
        .finally(() => {
          setSaving(false);
        });
    }
  };

  return (
    <div>
      {loading ? (
        <div className="tft-loading">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <>
          <div className="tft-header">
            <button
              className="btn btn-outline-primary btn-sm tft-add-btn"
              onClick={handleAddNew}
            >
              <MdAdd size={16} /> Add New
            </button>
          </div>

          {showForm && (
            <div className="tft-form-container">
              <div className="tft-form-header">
                <span className="tft-form-title">
                  {editingIndex !== null ? "Edit Entry" : "Add New Entry"}
                </span>
                <button onClick={handleCancel} className="tft-close-btn">
                  <MdClose size={18} />
                </button>
              </div>
              <div className="tft-form-row">
                <div className="tft-field-col">
                  <div className="tft-field-label">
                    <span>Category</span>
                  </div>
                  <input
                    type="text"
                    value={typeValue}
                    onChange={(e) => setTypeValue(e.target.value)}
                    placeholder="Enter a Category"
                    className="form-control form-control-sm tft-field-input"
                  />
                </div>
                <div className="tft-field-col tft-field-col--reasons">
                  <div className="tft-field-label">
                    <span>Reasons</span>
                  </div>
                  <div className="tft-reasons-box">
                    {reasons.map((r, idx) => (
                      <span
                        key={idx}
                        draggable
                        onDragStart={(e) => handleReasonDragStart(e, idx)}
                        onDragOver={(e) => handleReasonDragOver(e, idx)}
                        onDrop={(e) => handleReasonDrop(e, idx)}
                        onDragEnd={handleReasonDragEnd}
                        className={
                          "tft-reason-chip" +
                          (dragOverIndex === idx
                            ? " tft-reason-chip--drag-over"
                            : "") +
                          (dragIndex === idx
                            ? " tft-reason-chip--dragging"
                            : "")
                        }
                      >
                        {r}
                        <button
                          type="button"
                          onClick={() => handleRemoveReason(idx)}
                          className="tft-reason-remove"
                        >
                          <MdClose size={14} />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={reasonInput}
                      onChange={(e) => setReasonInput(e.target.value)}
                      onKeyDown={handleReasonKeyDown}
                      placeholder={
                        reasons.length === 0
                          ? "Type a reason and press Enter"
                          : "Add another..."
                      }
                      className="tft-reason-input"
                    />
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm tft-save-btn"
                  onClick={handleSave}
                  disabled={saving || reasons.length === 0}
                >
                  {saving
                    ? "Saving..."
                    : editingIndex !== null
                      ? "Update"
                      : "Save"}
                </button>
              </div>
            </div>
          )}

          {rows.length > 0 ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow className="tft-table-header-row">
                    <TableCell className="tft-table-cell--header">
                      <div className="tft-header-cell-content">
                        Category
                        <button
                          type="button"
                          className="tft-info-btn"
                          title="Learn more"
                          onClick={(e) => {
                            console.log("Info button clicked");
                            e.stopPropagation();
                            setInfoModalData({
                              title: "Category",
                              content:
                                "The ticket category groups related reasons together (e.g. Return, Refund, Warranty). It appears as a dropdown option when users submit tickets.",
                              image: "/reason_from_brand.png",
                            });
                          }}
                        >
                          <MdInfoOutline size={15} />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="tft-table-cell--header">
                      <div className="tft-header-cell-content">Reasons</div>
                    </TableCell>
                    <TableCell className="tft-table-cell--actions">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="tft-chips-wrapper">
                          {(row.types || []).map((t, i) => (
                            <span key={i} className="tft-chip">
                              {t.label}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="tft-chips-wrapper">
                          {row.reasons.map((r, i) => (
                            <span key={i} className="tft-chip">
                              {r?.reason || r}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="tft-actions-cell">
                        <div className="tft-actions-wrapper">
                          <button
                            className="btn btn-outline-secondary btn-sm tft-action-btn"
                            onClick={() => handleEdit(idx)}
                          >
                            <MdEdit size={13} /> Edit
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm tft-action-btn"
                            onClick={() => handleDelete(idx)}
                          >
                            <MdDelete size={13} /> Delete
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="tft-empty">
              <p className="tft-empty-title">No ticket types configured</p>
              <p className="tft-empty-subtitle">
                Add ticket types and reasons for this brand
              </p>
            </div>
          )}
        </>
      )}

      <InfoModal
        isOpen={!!infoModalData}
        onClose={() => setInfoModalData(null)}
        title={infoModalData?.title}
        content={infoModalData?.content}
        image={infoModalData?.image}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        categoryName={
          deleteTarget?.row?.types?.length > 0
            ? deleteTarget.row.types[0].label
            : "Unknown"
        }
        onConfirm={handleConfirmDelete}
        deleting={deleting}
      />
    </div>
  );
};

export default TicketFormTab;
